// src/components/Sidebar/useProjects.js
import { useEffect, useRef, useState, useCallback } from "react";
import axiosInstace from "../../axiosInstace";
import { subscribeToProjects } from "../../ws/wsManager";

const PROJECTS_ENDPOINT = "/api/emp/project/employee/allproject";

function publishProjects(list) {
  // Kept identical to the original global broadcast contract so
  // NotificationCenter and anything else listening for
  // "allprojects-ready" / window.__ALL_PROJECTS__ keeps working unchanged.
  window.__ALL_PROJECTS__ = list;
  window.dispatchEvent(
    new CustomEvent("allprojects-ready", { detail: { projects: list } }),
  );
}

/**
 * Owns the project list: initial load, WS subscription, and refresh
 * on the "project-updated" event. Pulled out of the component so the
 * component tree only deals with rendering.
 */
export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstace.get(PROJECTS_ENDPOINT);
      if (!mountedRef.current) return;

      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setProjects(list);
      publishProjects(list);
      subscribeToProjects(list.map((p) => p.projectId));
    } catch (err) {
      console.error("Sidebar: failed to load projects", err);
      // Preserve original behavior: a failed *initial* load clears the
      // list; a failed background refresh (silent) leaves the last
      // known-good list on screen instead of blanking the sidebar.
      if (mountedRef.current && !silent) setProjects([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();

    const onProjectUpdated = (ev) => {
      console.log("[Sidebar] project-updated:", ev?.detail);
      refresh({ silent: true }).catch(() => {
        console.warn("Sidebar: failed to refresh projects after project-updated");
      });
    };

    window.addEventListener("project-updated", onProjectUpdated);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("project-updated", onProjectUpdated);
    };
  }, [refresh]);

  return { projects, loading, refresh };
}
